import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';
import { capitalize } from 'es-toolkit/string';

import useToast, { type ToastData } from '~/hooks/useToast';

interface DeleteIdentityResponse {
  toast?: ToastData;
}

interface UseDeleteIdentityProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface ReturnProps {
  deleteIdentity: (identityId: string, providerName?: string) => void;
  isDeleting: boolean;
  deletingIdentityId: string | null;
}

const useDeleteIdentity = ({ onSuccess, onError }: UseDeleteIdentityProps = {}): ReturnProps => {
  const { t } = useTranslation('common', { keyPrefix: 'connected_accounts_modal.disconnect' });
  const fetcher = useFetcher<DeleteIdentityResponse>();
  const [deletingIdentityId, setDeletingIdentityId] = useState<string | null>(null);

  useToast(fetcher.data?.toast);

  const deleteIdentity = useCallback(
    (identityId: string, providerName?: string) => {
      setDeletingIdentityId(identityId);

      const formData = new FormData();
      formData.append('identity_id', identityId);

      const capitalizedProvider = capitalize(providerName || 'integration');

      formData.append('success_message', t('success', { providerName: capitalizedProvider }));
      formData.append('error_message', t('error', { providerName: capitalizedProvider }));

      fetcher.submit(formData, {
        method: 'POST',
        action: '/delete_identity',
      });
    },
    [fetcher, t],
  );

  useEffect(() => {
    if (fetcher.state !== 'idle' || !deletingIdentityId) return;

    const toast = fetcher.data?.toast;

    if (toast?.status === 'success') {
      onSuccess?.();
    } else if (toast?.status === 'error') {
      onError?.(toast.message);
    } else {
      onError?.(t('unknown_error'));
    }

    setDeletingIdentityId(null);
  }, [fetcher.state, fetcher.data, deletingIdentityId, onSuccess, onError, t]);

  return {
    deleteIdentity,
    isDeleting: fetcher.state === 'submitting' || fetcher.state === 'loading',
    deletingIdentityId,
  };
};

export default useDeleteIdentity;
